import {Injectable, signal} from '@angular/core';
import Keycloak, {KeycloakProfile, KeycloakTokenParsed} from 'keycloak-js';
import {keycloakConfig, keycloakInitOptions} from "../auth.config";
import {PermissionsOperation, ProfileRoles} from "../app-permissions";
import {User} from "../../../features/users/user";
import {environment} from "../../../../environments/environment";

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private keycloak: Keycloak;
    private _apiUser = signal<User | null>(null);
    readonly apiUser = this._apiUser.asReadonly();

    constructor() {
        this.keycloak = new Keycloak(keycloakConfig);
    }

    public init(): Promise<boolean> {
        return this.keycloak
            .init(keycloakInitOptions)
            .then(authenticated => {
                if (authenticated) {
                    this.scheduleRefreshToken();
                }
                return authenticated;
            });
    }

    private scheduleRefreshToken(): void {
        setInterval(() => {
            this.keycloak
                .updateToken(60)
                .then(refreshed => {
                    if (refreshed) {
                        console.log('Auth: Token refreshed');
                    }
                })
                .catch(() => {
                    console.warn('Auth: Could not refresh token');
                });
        }, 60_000);
    }

    public redirectToLogin(): void {
        this.keycloak.login({redirectUri: environment.auth.redirectUri});
    }

    public logout(): void {
        this._apiUser.set(null);
        this.keycloak.logout({redirectUri: environment.auth.redirectUri});
    }

    public isLoggedIn(): Promise<boolean> {
        return Promise.resolve(this.keycloak.authenticated || false);
    }

    public getToken(): Promise<string | undefined> {
        return Promise.resolve(this.keycloak.token);
    }

    public loadUserProfile(): Promise<KeycloakProfile> {
        return this.keycloak.loadUserProfile();
    }

    public getUserRoles(): string[] | undefined {
        const token: KeycloakTokenParsed | undefined = this.keycloak.tokenParsed;
        if (token) {
            return token?.realm_access?.roles ?? token["roles"];
        }
        return undefined;
    }

    public hasRole(role: ProfileRoles): boolean {
        const roles = this.getUserRoles();
        if (roles) {
            return roles.includes(role);
        }
        return false;
    }

    public hasRoles(
        roles: ProfileRoles[],
        operation: PermissionsOperation
    ): boolean {
        if (operation === 'and') {
            return roles.every((role) => this.hasRole(role));
        } else {
            return roles.some((role) => this.hasRole(role));
        }
    }

    public refreshToken(): Promise<boolean> {
        return this.keycloak.updateToken(-1);
    }

    public async refreshTokenWithNewClaims(): Promise<void> {
        this.keycloak.clearToken();
        await this.keycloak.login({
            prompt: 'none',
            redirectUri: window.location.href
        });
    }

    getApiUser(): User | null {
        return this._apiUser();
    }

    setApiUser(user: User | null): void {
        this._apiUser.set(user);
    }
}
